import { Row, Col, FormGroup, Label, ButtonGroup, Button } from 'reactstrap';
import { Field } from 'formik';
import { IAccessLevel, IMap, SelectOption, JobTitle, SetFieldValue } from '../../globals/interfaces';
import styled from 'styled-components';
import { IUnitMap, isChoiceEmail } from '../../utils';
import Switch from 'rc-switch';
import Cleave from "cleave.js/react";
import Select from '../Select';
import { DEPARTMENTS } from './constants';

const QUEUE_FILTER_OPTIONS = [
    '',
    'ALL',
    'IMPORT',
    'EXPORT',
    'TRANSFERS'
]

interface Props {
    values: any;
    uniqueEmail: (s_email: string) => boolean;
    uniqueNumber: (i_employee_number: string) => boolean;
    unitsMap: IMap<IUnitMap>;
    selectedUnits: Array<string>;
    setFieldValue: SetFieldValue;
    handleSelectUnit: (unit: string, setFieldValue: SetFieldValue) => void;
    newEmployee: boolean;
    statusOptions: Array<string>;
    restrictedAccess: boolean;
    sortedAccessLevels: Array<IAccessLevel>;
    handleModalAirlines: () => Promise<void>;
    ipAddresses: Record<string, string>;
    handleCreateUpdateIp: (create: boolean, address: string, updateIndex: string) => void;
    setManageAccessMap: React.Dispatch<React.SetStateAction<boolean>>;
    setManageNativeAccessMap: React.Dispatch<React.SetStateAction<boolean>>;
    jobTitles: Array<JobTitle>;
}

export default function EmployeeForm({
    values,
    uniqueEmail,
    uniqueNumber,
    unitsMap,
    selectedUnits,
    setFieldValue,
    handleSelectUnit,
    newEmployee,
    statusOptions,
    restrictedAccess,
    sortedAccessLevels,
    handleModalAirlines,
    ipAddresses,
    handleCreateUpdateIp,
    setManageAccessMap,
    setManageNativeAccessMap,
    jobTitles
}: Props) {

    const workTypeOptions = [
        '',
        'FULL TIME',
        'PART TIME',
        'TEMPORARY',
        'SEASONAL',
        'CONTRACTOR',
        'FREELANCER',
        'CONSULTANT',
    ];

    const serviceTypeOptions = [
        '',
        'CARGO',
        'PASSENGER'
    ];

    const disabledAccessSettings = (values: any) =>
        ['TERMINATED', 'SUSPENDED'].includes(values.s_status);

    const jobTitleOptions: Array<SelectOption<number>> = jobTitles.map(item => ({ label: item.title, value: item.id }));
    const departmentOptions: Array<SelectOption> = DEPARTMENTS.map(item => ({
        label: item,
        value: item
    }));

    const { b_internal } = values;
    const disableEditing = b_internal && !newEmployee;

    return (
        <Row>
            <Col md={6}>
                <FormGroup>
                    <Label>
                        Email{' '}
                        <span className={'bg-warning text-danger'}>
                            {!uniqueEmail(values.s_email) &&
                                'Email already exists'}
                        </span>
                    </Label>
                    <Field
                        type="email"
                        name="s_email"
                        className="form-control"
                        disabled={!newEmployee}
                    />
                </FormGroup>
                <FlexContainer>
                    <FormGroup>
                        <Label className="d-block">Access Unit</Label>
                        <UnitsContainer>
                            {Object.keys(unitsMap).map((key, i) => (
                                <div key={i}>
                                    {unitsMap[key].array.map((unit, j) => (
                                        <UnitContainer
                                            key={`${unit}-${j}`}
                                            selected={selectedUnits.includes(unit)}
                                            color={unitsMap[key].color}
                                            onClick={() =>
                                                handleSelectUnit(
                                                    unit,
                                                    setFieldValue
                                                )
                                            }
                                        >
                                            {unit}
                                        </UnitContainer>
                                    ))}
                                </div>
                            ))}
                        </UnitsContainer>
                    </FormGroup>
                    <FormGroup>
                        <Label className="d-block">Home Station</Label>
                        <UnitsContainer>
                            {Object.keys(unitsMap).map((key, i) => (
                                <div key={i}>
                                    {unitsMap[key].array.map((unit, j) => (
                                        <UnitContainer
                                            key={`${unit}-${j}`}
                                            selected={values.s_home_station === unit}
                                            color={unitsMap[key].color}
                                            onClick={() =>
                                                setFieldValue(
                                                    's_home_station',
                                                    unit
                                                )
                                            }
                                        >
                                            {unit}
                                        </UnitContainer>
                                    ))}
                                </div>
                            ))}
                        </UnitsContainer>
                    </FormGroup>
                </FlexContainer>
                <FormGroup>
                    <Label>
                        Employee Number
                        <span className={'bg-warning text-danger'}>
                            {!uniqueNumber(values.i_employee_number) &&
                                'Number already exists'}
                        </span>
                    </Label>
                    <Field
                        type="number"
                        name="i_employee_number"
                        className="form-control"
                    />
                </FormGroup>
                <FormGroup>
                    <Label>First Name</Label>
                    <Field
                        type="text"
                        name="s_first_name"
                        className="form-control"
                        disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Middle Name</Label>
                    <Field
                        type="text"
                        name="s_middle_name"
                        className="form-control"
                        disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Last Name</Label>
                    <Field
                        type="text"
                        name="s_last_name"
                        className="form-control"
                        disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Date of Hire</Label>
                    <Field type="date" name="d_hire" className="form-control" disabled={disableEditing} />
                </FormGroup>
            </Col>
            <Col md={6}>
                <FormGroup>
                    <Label>Work Type</Label>
                    <Field
                        component="select"
                        name="s_work_type"
                        className="form-control"
                        disabled={disableEditing}
                    >
                        {workTypeOptions.map((type, i) => (
                            <option value={type} key={i}>
                                {type}
                            </option>
                        ))}
                    </Field>
                </FormGroup>
                <FormGroup>
                    <Label>Service Type</Label>
                    <Field
                        component="select"
                        name="s_service_type"
                        className="form-control"
                        disabled={disableEditing}
                    >
                        {serviceTypeOptions.map((type, i) => (
                            <option value={type} key={i}>
                                {type}
                            </option>
                        ))}
                    </Field>
                </FormGroup>
                <FormGroup>
                    <Label>Phone Number</Label>
                    <Field
                        name="s_phone_num"
                        component={PhoneNumberInput}
                        options={{
                            delimiters: ["-", "-"],
                            blocks: [3, 3, 4],
                            numericOnly: true,
                        }}
                        className="form-control"
                        disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Job Title</Label>
                    <Select 
                        options={jobTitleOptions}
                        value={values.i_job_id}
                        onChange={option => setFieldValue('i_job_id', option.value)}
                        // disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Department</Label>
                    <Select 
                        options={departmentOptions}
                        value={values.s_department}
                        onChange={option => setFieldValue('s_department', option.value)}
                        // disabled={disableEditing}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Status:</Label>
                    <Field
                        component="select"
                        name="s_status"
                        className="form-control"
                    >
                        {newEmployee ? (
                            <option value={statusOptions[0]}>
                                {statusOptions[0]}
                            </option>
                        ) : (
                            statusOptions.map((o, i) => (
                                <option value={o} key={i}>
                                    {o}
                                </option>
                            ))
                        )}
                    </Field>
                </FormGroup>
                {values.s_status &&
                    values.s_status.toUpperCase() !== 'ACTIVE' && (
                        <FormGroup>
                            {values.s_status === 'TERMINATED' ? (
                                <>
                                    <Label>Termination Date</Label>
                                    <Field
                                        type="date"
                                        name="d_terminated"
                                        className="form-control"
                                    />
                                </>
                            ) : values.s_status === 'SUSPENDED' ? (
                                <>
                                    <Label>Suspension Start</Label>
                                    <Field
                                        type="date"
                                        name="d_suspended_start"
                                        className="form-control mb-2"
                                    />
                                    <Label>Suspension End</Label>
                                    <Field
                                        type="date"
                                        name="d_suspended_end"
                                        className="form-control"
                                    />
                                </>
                            ) : (
                                <>
                                    <Label>Furlough Start</Label>
                                    <Field
                                        type="date"
                                        name="d_furlough_start"
                                        className="form-control mb-2"
                                    />
                                    <Label>Furlough End</Label>
                                    <Field
                                        type="date"
                                        name="d_furlough_end"
                                        className="form-control"
                                    />
                                </>
                            )}
                        </FormGroup>
                    )}
                {restrictedAccess && (
                    <>
                        <FormGroup>
                            <Label>Access Level</Label>
                            <Row>
                                <Col md={12}>
                                    <ButtonGroup>
                                        {sortedAccessLevels.map((l, i) => (
                                            <Button
                                                key={i}
                                                active={
                                                    values.i_access_level ===
                                                    l.i_access_level
                                                }
                                                onClick={() =>
                                                    setFieldValue(
                                                        'i_access_level',
                                                        l.i_access_level
                                                    )
                                                }
                                            >
                                                {l.i_access_level}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </FormGroup>
                        <FlexContainer>
                            {!newEmployee && (
                                <div>
                                    <Row
                                        className={`${disabledAccessSettings(values) &&
                                            'custom-disabled'
                                            }`}
                                    >
                                        <Col md={12}>
                                            <Label>
                                                Global IP Address Access{' '}
                                                <span className={'bg-warning'}>
                                                    {disabledAccessSettings(values) &&
                                                        'Only available for ACTIVE employees'}
                                                </span>
                                            </Label>
                                            <Switch
                                                className="custom-switch custom-switch-primary"
                                                checked={values.b_global_ip_access}
                                                onClick={() =>
                                                    setFieldValue(
                                                        'b_global_ip_access',
                                                        !values.b_global_ip_access
                                                    )
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}
                            <div>
                                <FormGroup>
                                    <Label>Internal</Label>
                                    <Switch
                                        className="custom-switch custom-switch-primary"
                                        checked={isChoiceEmail(values.s_email)}
                                    />
                                </FormGroup>
                            </div>
                            <div>
                                <FormGroup>
                                    <Label>HR Admin</Label>
                                    <Switch
                                        className="custom-switch custom-switch-primary"
                                        checked={values.b_hr_admin}
                                        onClick={() =>
                                            setFieldValue(
                                                'b_hr_admin',
                                                !values.b_hr_admin
                                            )
                                        }
                                    />
                                </FormGroup>
                            </div>
                            <div>
                                <FlexContainer width={200}>
                                    <div>
                                        <Label>Airline</Label>
                                        <Switch
                                            className="custom-switch custom-switch-primary"
                                            checked={values.b_airline}
                                            onClick={() =>
                                                setFieldValue(
                                                    'b_airline',
                                                    !values.b_airline
                                                )
                                            }
                                        />
                                    </div>
                                    {values.b_airline && (
                                        <button
                                            className={'mt-4 btn btn-outline-dark'}
                                            onClick={() => handleModalAirlines()}
                                        >
                                            Manage Airlines
                                        </button>
                                    )}
                                </FlexContainer>
                            </div>
                        </FlexContainer>
                    </>
                )}
                {!newEmployee && (
                    <AccessButtonsContainer
                        className={`mt-2 ${disabledAccessSettings(values) && 'custom-disabled'
                            }`}
                    >
                        <Button onClick={() => setManageAccessMap(true)}>
                            Access Map
                        </Button>
                        <Button onClick={() => setManageNativeAccessMap(true)}>
                            Mobile Access Map
                        </Button>
                    </AccessButtonsContainer>
                )}
                {
                    !newEmployee && (
                        <div className={'mt-4'}>
                            <Label>Queue Filter Setting (allows an agent to Select the first company within these filters)</Label>
                            <Field
                                component="select"
                                name="s_queue_filter"
                                className="form-control"
                            >
                                {QUEUE_FILTER_OPTIONS.map((o, i) => (
                                    <option value={o} key={i}>
                                        {o}
                                    </option>
                                ))}
                            </Field>
                        </div>
                    )
                }
            </Col>
        </Row>
    );
}

const PhoneNumberInput = ({ field, form, ...props }) => {
    return (
        <Cleave
            {...field}
            {...props}
            onChange={(event) => form.setFieldValue(field.name, event.target.value)}
        />
    );
};

const FlexContainer = styled.div<{ width?: number }>`
    display: flex;
    justify-content: space-between;
    width: ${p => p.width + 'px'};
`;

const UnitsContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const UnitContainer = styled.div<{ selected: boolean }>`
    background-color: ${(p) => (p.selected ? 'gold' : p.color)};
    border: 1px solid black;
    border-radius: 12px;
    text-align: center;
    margin: 5px 0px;
    padding: 10px;
    transition: 200ms;
    font-weight: bold;

    &:hover {
        cursor: pointer;
        transform: scale(1.1);
    }
`;

const AccessButtonsContainer = styled.div`
    display: flex;
    gap: 10px;
`;
